/**
 * Team-portal home — the `/app/:tenantSlug` index every persona can land on.
 *
 * Three real blocks, no scaffolding:
 *   1. the session's active event (from `ActiveEventProvider`) with the
 *      actions that matter during a game,
 *   2. recent events from `GET /events/v1` (newest first) linking into the
 *      Game Summary,
 *   3. the persona's own navigation as quick links — the same model the
 *      sidebar uses, so nothing appears here the gateway would refuse.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useActiveEvent } from '../../app/active-event-context';
import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { DeskSection } from '../../components/desk-section';
import { PageHeader } from '../../components/page-header';
import { StatusPill, type StatusTone } from '../../components/status-pill';
import type { TeamEvent } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';
import { formatEventDate, formatEventLabel } from '../../lib/event-format';
import { navItemsFor } from '../../lib/nav-items';

const RECENT_LIMIT = 6;

/** Active = live now (green), pending = scheduled (info), closed = done. */
const STATUS_TONES: Record<string, StatusTone> = {
  active: 'success',
  pending: 'info',
  closed: 'neutral',
};

export function HomePage(): JSX.Element {
  const { persona, tenantSlug } = useSession();
  const { activeEvent } = useActiveEvent();
  const apiClient = useApiClient();

  const [recent, setRecent] = useState<TeamEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const page = await apiClient.listEvents(
          { limit: RECENT_LIMIT, sort_by: 'scheduled_at', sort_order: 'desc' },
          controller.signal
        );
        setRecent(page.items);
        setError(null);
      } catch {
        if (controller.signal.aborted) return;
        setError('Recent events could not be loaded right now.');
        setRecent([]);
      }
    })();
    return () => controller.abort();
  }, [apiClient]);

  const base = `/app/${tenantSlug ?? ''}`;
  const links = persona === null ? [] : navItemsFor(persona).filter((item) => item.view !== '');
  const upcoming =
    recent?.filter(
      (event) => event.status === 'pending' && event.event_id !== activeEvent?.event_id
    ) ?? [];
  const nextUp =
    upcoming.length === 0
      ? null
      : upcoming.reduce((soonest, event) =>
          (event.scheduled_at ?? '') < (soonest.scheduled_at ?? '') ? event : soonest
        );

  return (
    <div className="home" data-testid="portal-home">
      <PageHeader
        className="home__head"
        title="Team home"
        context="Pick up the current game or jump to a view."
      />

      {persona === 'team_admin' ? (
        <p className="home__notice" aria-label="Team setup">
          New here? The <Link to={`${base}/welcome`}>setup wizard</Link> walks team info, staff
          invites and your first plays in a few minutes.
        </p>
      ) : null}

      <div className="home__grid">
        <div className="home__col">
          <DeskSection
            title="Current event"
            className="home__current"
            actions={
              activeEvent === null ? null : (
                <StatusPill tone={STATUS_TONES[activeEvent.status] ?? 'neutral'}>
                  {activeEvent.status}
                </StatusPill>
              )
            }
          >
            {activeEvent !== null ? (
              <div className="home__active">
                <div className="home__active-main">
                  <span className="home__active-name">{activeEvent.name}</span>
                  <span className="muted">{formatEventLabel(activeEvent)}</span>
                </div>
                <div className="home__active-actions">
                  <Link className="btn btn--primary" to={`${base}/${APP_VIEWS.playEntry}`}>
                    Play Entry
                  </Link>
                  <Link className="btn btn--secondary" to={`${base}/${APP_VIEWS.gameSummary}`}>
                    Game Summary
                  </Link>
                </div>
              </div>
            ) : (
              <p className="home__empty muted">
                No event selected. Choose one from the event switcher above, or open the{' '}
                <Link to={`${base}/${APP_VIEWS.events}`}>Event Manager</Link> to schedule the next
                game.
              </p>
            )}
            {nextUp === null ? null : (
              <p className="home__next">
                <span className="home__next-label">Next up</span>
                <Link to={`${base}/${APP_VIEWS.events}/${nextUp.event_id}`}>{nextUp.name}</Link>
                <span className="muted">{formatEventDate(nextUp.scheduled_at)}</span>
              </p>
            )}
          </DeskSection>
          <DeskSection
            title="Recent events"
            className="home__recent"
            actions={
              <Link className="btn btn--ghost btn--sm" to={`${base}/${APP_VIEWS.events}`}>
                All events
              </Link>
            }
          >
            {recent === null ? (
              <p className="home__empty muted" role="status">
                Loading recent events…
              </p>
            ) : recent.length === 0 ? (
              <p className="home__empty muted">
                {error ?? 'No events yet — schedule the first one to get going.'}
              </p>
            ) : (
              <ul className="home__events">
                {recent.map((event) => (
                  <li key={event.event_id}>
                    <Link
                      className="home__event"
                      to={`${base}/${APP_VIEWS.events}/${event.event_id}`}
                    >
                      <span className="home__event-name">{event.name}</span>
                      <span className="home__event-date muted">
                        {formatEventDate(event.scheduled_at)}
                      </span>
                      <StatusPill tone={STATUS_TONES[event.status] ?? 'neutral'}>
                        {event.status}
                      </StatusPill>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </DeskSection>
        </div>

        <DeskSection title="Your views" className="home__views">
          <ul className="home__links">
            {links.map((item) => (
              <li key={item.id}>
                <Link className="home__link" to={`${base}/${item.view}`}>
                  <span aria-hidden="true" className="home__link-icon">
                    {item.icon}
                  </span>
                  <span className="home__link-label">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </DeskSection>
      </div>
    </div>
  );
}
